import { useEffect, useMemo, useState } from 'react';
import { useJob, useJobs, type Job } from '../../../api/jobs.api';

/* JobPicker — Sprint 16c.
   Searchable combobox over the org's JobCache. Selecting a job sets the
   campaign's bound_job_id; "Unbind" clears it back to manual fill mode.

   Split into a hook-aware outer wrapper and a pure inner so the unit
   test can render the inner directly without a QueryClientProvider — same
   pattern as CalendarSlotPicker (Sprint 16a). */

export interface JobPickerProps {
  value: string | null;
  onChange: (next: string | null) => void;
}

export interface JobPickerInnerProps extends JobPickerProps {
  /** Search results — usually `useJobs(query).data?.data ?? []`. */
  options: Job[];
  /** Optional pre-loaded current value's full row (so we can render
   *  "Bound to: Title · Location" even before the search runs). */
  currentJob?: Job | null;
  onQuery?: (q: string) => void;
}

const DEBOUNCE_MS = 300;

/** Hook-aware outer wrapper. */
export function JobPicker({ value, onChange }: JobPickerProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const search = useJobs({ q: debouncedQuery || undefined, limit: 25 });
  const current = useJob(value);

  return (
    <JobPickerInner
      value={value}
      onChange={onChange}
      options={search.data?.data ?? []}
      currentJob={current.data?.data ?? null}
      onQuery={setQuery}
    />
  );
}

/** Pure presentational inner. */
export function JobPickerInner({ value, onChange, options, currentJob, onQuery }: JobPickerInnerProps) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  // Show all options when no query; otherwise let the parent's search hit
  // do the filtering. Either way, dedup by jt_job_id.
  const visible = useMemo(() => {
    const seen = new Set<string>();
    return options.filter((j) => {
      if (seen.has(j.jt_job_id)) return false;
      seen.add(j.jt_job_id);
      return true;
    });
  }, [options]);

  const handlePick = (job: Job) => {
    onChange(job.jt_job_id);
    setOpen(false);
    setQ('');
    onQuery?.('');
  };

  // Bound state: show the current job + an Unbind affordance.
  if (value && currentJob) {
    return (
      <div data-testid="job-picker-bound" style={boundShell()}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-brand, #1e1b4b)' }}>
            {currentJob.title ?? '(untitled job)'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted, #6b6b7d)' }}>
            {currentJob.location ?? '-'}
            {currentJob.status ? <span style={statusChip()}> {currentJob.status} </span> : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          data-testid="job-picker-unbind"
          style={ghost()}
        >
          Unbind
        </button>
      </div>
    );
  }

  // Bound but the row hasn't loaded yet (e.g. stale id, no JT sync).
  // Show a fallback that still lets the user unbind without waiting.
  if (value && !currentJob) {
    return (
      <div data-testid="job-picker-bound-loading" style={boundShell()}>
        <div style={{ flex: 1, fontSize: 13, color: 'var(--color-text-muted)' }}>
          Bound to <code style={code()}>{value}</code>, but this job isn't in Engage's copy of your JobTetris data. Run JobTetris sync, or unbind.
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          data-testid="job-picker-unbind"
          style={ghost()}
        >
          Unbind
        </button>
      </div>
    );
  }

  // Unbound: search + list.
  return (
    <div data-testid="job-picker" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input
        type="search"
        value={q}
        placeholder="Search jobs by title or location…"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          onQuery?.(e.target.value);
        }}
        onFocus={() => setOpen(true)}
        data-testid="job-picker-search"
        style={inputStyle()}
      />
      {open && (
        <ul
          role="listbox"
          data-testid="job-picker-options"
          style={{
            margin: 0,
            padding: 4,
            listStyle: 'none',
            border: '1px solid var(--color-border-default, #ececf2)',
            borderRadius: 6,
            background: '#fff',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {visible.length === 0 ? (
            <li style={{ padding: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
              No matching jobs in the cache. Run JobTetris sync if you expect to see one.
            </li>
          ) : (
            visible.map((j) => (
              <li key={j.jt_job_id}>
                <button
                  type="button"
                  onClick={() => handlePick(j)}
                  data-testid={`job-picker-pick-${j.jt_job_id}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: 13,
                    color: 'var(--color-text-secondary)',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-blue-50, #e8edff)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{ fontWeight: 600, color: 'var(--color-text-brand, #1e1b4b)' }}>
                    {j.title ?? '(untitled)'}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted, #6b6b7d)' }}>
                    {j.location ?? '-'} {j.status ? `· ${j.status}` : ''}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    padding: '6px 10px',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'inherit',
  };
}

function boundShell(): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    border: '1px solid var(--color-brand-blue, #3b5bdb)',
    background: 'var(--color-blue-50, #e8edff)',
    borderRadius: 6,
  };
}

function ghost(): React.CSSProperties {
  return {
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
    background: '#fff',
    color: 'var(--color-text-muted, #6b6b7d)',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 4,
    cursor: 'pointer',
  };
}

function statusChip(): React.CSSProperties {
  return {
    marginLeft: 8,
    padding: '0 6px',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.04,
    background: '#fff',
    color: 'var(--color-text-muted, #6b6b7d)',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 999,
  };
}

function code(): React.CSSProperties {
  return {
    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
    fontSize: 11,
    background: '#fff',
    padding: '1px 4px',
    borderRadius: 3,
  };
}
